import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
  wrap,
} from 'framer-motion';
import Image from 'next/image';
import React, { useRef } from 'react';

import type {
  LocalizedPartner,
  LocalizedPartnerGroup,
} from '@/data/shared/partners';

import { PARTNER_MARK_MOBILE_SCALE, partnerMarkSize } from './partnerMarkSize';

/**
 * Share of one copy's width a band drifts per second while the page is
 * still: slow enough that a mark can be read as it goes by, since the
 * scroll is what brings the pace up.
 */
const RESTING_SPEED = 1.1;

/**
 * How much scrolling adds to that drift. The page's velocity is taken as a
 * magnitude, so scrolling either way speeds a band up rather than turning
 * it around: each band keeps the direction it was given and only its pace
 * answers the scroll. Mapped well past the resting speed so a flick is
 * felt, and left unclamped so a hard scroll keeps adding.
 */
const SCROLL_INFLUENCE = 6;

/**
 * ### PartnerMark
 *
 * One organisation in a band, standing on the page rather than inside a
 * box. Where the mark has not arrived yet the name is set as type in its
 * place, which needs no label of its own.
 *
 * With a pointer that can hover, the mark is shown in grey and takes its
 * colour back under the cursor, naming itself above as it does. A coarse
 * pointer has no hover to give, so there the mark is simply in colour and
 * carries its name under it: a name reachable only by hovering is no name
 * at all on a phone.
 *
 * @param props.partner The organisation to render
 * @returns The rendered cell
 */
function PartnerMark({ partner }: { partner: LocalizedPartner }) {
  if (!partner.logo) {
    return (
      <li className="flex h-[72px] w-[220px] shrink-0 items-center justify-center px-4 lg:h-[88px] lg:w-[280px]">
        <span className="text-balance text-center text-[15px] font-bold leading-[135%] tracking-[-0.3px] text-apps-coreGray02 lg:text-[16px]">
          {partner.name}
        </span>
      </li>
    );
  }

  const size = partnerMarkSize(partner.logo.aspectRatio);

  return (
    <li className="group relative flex h-[72px] w-[220px] shrink-0 items-center justify-center px-4 lg:h-[88px] lg:w-[280px]">
      {/*
       * Drawn at a size taken from the mark's own shape rather than stretched
       * to the cell, so marks of every proportion weigh the same; see
       * `partnerMarkSize`. The CSS variables carry the desktop size and the
       * mobile wall scales it down, never past the cell.
       */}
      <span
        className="relative h-[calc(var(--mark-h)*var(--mark-mobile-scale))] w-[min(calc(var(--mark-w)*var(--mark-mobile-scale)),100%)] lg:h-[var(--mark-h)] lg:w-[var(--mark-w)]"
        style={
          {
            '--mark-w': `${size.width}px`,
            '--mark-h': `${size.height}px`,
            '--mark-mobile-scale': PARTNER_MARK_MOBILE_SCALE,
          } as React.CSSProperties
        }
      >
        <Image
          className="object-contain opacity-60 grayscale transition duration-300 ease-out group-hover:opacity-100 group-hover:grayscale-0 pointer-coarse:opacity-100 pointer-coarse:grayscale-0"
          src={`/images/apps/home/${partner.logo.file}`}
          alt={partner.name}
          fill
          sizes={`${size.width}px`}
        />
      </span>
      {/*
       * The name, for a mark that does not spell it out. It repeats the
       * image's own alt text, so it is decoration to a screen reader and is
       * hidden from one.
       *
       * On a hovering pointer it is a label that rises above the mark on
       * demand; on a coarse one it drops below the mark, drops the pill it
       * was drawn in, and stays, since nothing there can summon it.
       */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-lg bg-apps-coreGrayBlack px-3 py-2 text-[13px] font-bold leading-none tracking-[-0.26px] text-apps-coreGrayWhite opacity-0 transition duration-200 ease-out group-hover:translate-y-0 group-hover:opacity-100 pointer-coarse:bottom-auto pointer-coarse:top-full pointer-coarse:mb-0 pointer-coarse:mt-2 pointer-coarse:translate-y-0 pointer-coarse:rounded-none pointer-coarse:bg-transparent pointer-coarse:px-0 pointer-coarse:py-0 pointer-coarse:text-apps-coreGray02 pointer-coarse:opacity-100"
      >
        {partner.name}
      </span>
    </li>
  );
}

interface AboutPartnerRowProps {
  group: LocalizedPartnerGroup;
  /** Position of the band, which decides which way it drifts */
  index: number;
  /** Whether to render the group label above the moving band */
  showTitle?: boolean;
}

/**
 * ### AboutPartnerRow
 *
 * One band of the wall, drifting sideways and carrying the page's scroll
 * with it: at rest it moves at a walking pace, and scrolling speeds it up in
 * proportion to how fast the page is moving, whichever way the page goes.
 * The band never ends because the members are rendered twice and the offset
 * wraps over one copy's width, so the loop closes on an identical frame.
 *
 * Bands alternate direction down the wall and keep it: a band that reversed
 * with the scroll would make the wall lurch every time the reader changed
 * their mind. A band holds still while the pointer is over it, since a name
 * that slides out from under the cursor cannot be read. Under a
 * reduced-motion preference it never moves at all.
 *
 * @returns The rendered band
 */
export default function AboutPartnerRow({
  group,
  index,
  showTitle = true,
}: AboutPartnerRowProps) {
  const shouldReduceMotion = useReducedMotion();
  const restingDirection = index % 2 === 0 ? 1 : -1;

  const offset = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400,
  });

  /*
   * The magnitude of the page's velocity, not its sign: the band answers how
   * hard the page is being scrolled, never which way.
   */
  const velocityFactor = useTransform(
    smoothVelocity,
    (value) => Math.min(Math.abs(value) / 1000, 8) * SCROLL_INFLUENCE,
  );

  /*
   * One copy of the members is half the rendered width, so wrapping the
   * offset over -50%..0% puts an identical frame under the one that just
   * left.
   */
  const x = useTransform(offset, (value) => `${wrap(-50, 0, value)}%`);

  /*
   * Whether the band is held is asked of the DOM each frame rather than
   * tracked through pointer events. The band is under the pointer or it is
   * not, and reading that directly cannot fall out of step with the browser
   * the way a pair of enter and leave handlers can when the element moves
   * out from under a cursor that never itself moved.
   */
  const band = useRef<HTMLUListElement>(null);

  useAnimationFrame((_, delta) => {
    if (shouldReduceMotion || band.current?.matches(':hover')) return;

    const step = restingDirection * RESTING_SPEED * (delta / 1000);

    offset.set(offset.get() + step * (1 + velocityFactor.get()));
  });

  return (
    <div>
      {showTitle && (
        <p className="px-gutter text-[13px] font-bold uppercase leading-[140%] tracking-[0.65px] text-apps-coreGray02 lg:text-[15px] lg:tracking-[0.75px]">
          {group.title}
        </p>
      )}
      {/*
       * The band runs edge to edge and fades out at both sides. The vertical
       * padding is what a mark's name hangs in, above it or below it
       * depending on the pointer; without it the overflow that hides the
       * loop would cut the name off too.
       */}
      <div className="relative mt-4 overflow-hidden py-10 [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)] lg:mt-5">
        <motion.ul
          ref={band}
          className="flex w-max items-center gap-10 lg:gap-14"
          style={shouldReduceMotion ? undefined : { x }}
        >
          {group.members.map((partner) => (
            <PartnerMark key={partner.id} partner={partner} />
          ))}
          {/* The second pass is what the wrap lands on; it says nothing new */}
          {group.members.map((partner) => (
            <PartnerMark key={`echo-${partner.id}`} partner={partner} />
          ))}
        </motion.ul>
      </div>
    </div>
  );
}
